import type { ReactNode } from "react";
import { isApiError } from "@empite/api-client";
import { Alert, Badge, Card, Spinner } from "@empite/ui-components";
import type { BadgeTone } from "@empite/ui-components";
import { useViewer } from "../marketplace-hooks";
import type { CreationFile, CreationStatus, FileState, ModerationState } from "../marketplace-client";

/**
 * Where a signed-out visitor goes to sign in.
 *
 * There is no sign-in screen in this portal and there must not be: the
 * marketplace is the SAME identity realm as the customer portal and shares
 * its SSO cookie, so a second password prompt here would be a second password
 * for one person. The link is a plain origin with no `next`, because the
 * account portal only honours a SAME-ORIGIN return path — handing it one of
 * ours is the open-redirect it refuses by design.
 */
const ACCOUNT_PORTAL =
  (import.meta.env.VITE_ACCOUNT_PORTAL_URL as string | undefined)?.replace(/\/+$/, "") ??
  "http://localhost:5173";

export const SIGN_IN_URL = `${ACCOUNT_PORTAL}/sign-in`;

/** The human-facing text for anything thrown by the client. */
export function errorMessage(err: unknown, fallback = "Something went wrong. Try again."): string {
  if (isApiError(err)) {
    return err.message;
  }
  if (err instanceof Error && err.message !== "") {
    return err.message;
  }
  return fallback;
}

export function ErrorAlert({ error, title }: { error: unknown; title?: string }) {
  if (!error) {
    return null;
  }
  return (
    <Alert tone="danger" title={title ?? "That did not work"}>
      {errorMessage(error)}
    </Alert>
  );
}

/**
 * RequireSession renders its children only for a signed-in visitor.
 *
 * `ready` is checked before `signedIn` for the reason the customer portal
 * checks it: `signedIn` is false during the first session fetch too, so
 * acting on it alone shows the sign-in prompt to somebody who is signed in.
 */
export function RequireSession({ children }: { children: (userID: string) => ReactNode }) {
  const viewer = useViewer();
  if (!viewer.ready) {
    return <Spinner label="Checking your session" />;
  }
  if (!viewer.userID) {
    return <SignInPrompt />;
  }
  return <>{children(viewer.userID)}</>;
}

export function SignInPrompt({ what = "this page" }: { what?: string }) {
  return (
    <Card title="Sign in to continue">
      <p>
        <a href={SIGN_IN_URL}>Sign in to the suite</a> to see {what}. The marketplace uses the same
        account as every other application.
      </p>
    </Card>
  );
}

/**
 * The generic file placeholder.
 *
 * Phase 1 stores and returns arbitrary binaries WITHOUT ever interpreting
 * them: no thumbnails, no format sniffing, no archive listing, no conversion.
 * That is a policy (docs/architecture.md §18), not a gap waiting to be
 * filled, so this component takes the declared name and size and deliberately
 * has no branch on either.
 */
export function FilePlaceholder({ tile = false }: { tile?: boolean }) {
  return (
    <div className={tile ? "esm-file-placeholder esm-file-placeholder--tile" : "esm-file-placeholder"}>
      <span className="esm-file-placeholder__glyph" aria-hidden="true">
        FILE
      </span>
      <span className="esm-visually-hidden">A file. This portal does not preview file contents.</span>
    </div>
  );
}

export function statusTone(status: CreationStatus): BadgeTone {
  switch (status) {
    case "published":
      return "success";
    case "unpublished":
      return "warning";
    default:
      return "neutral";
  }
}

export function moderationTone(state: ModerationState): BadgeTone {
  switch (state) {
    case "visible":
      return "success";
    case "hidden":
      return "warning";
    default:
      return "danger";
  }
}

export function fileStateTone(state: FileState): BadgeTone {
  switch (state) {
    case "active":
      return "success";
    case "rejected":
    case "quarantined":
      return "danger";
    default:
      return "warning";
  }
}

/** What each file state means to the author, in their words rather than the
 *  state machine's. */
export function fileStateLabel(state: FileState): string {
  switch (state) {
    case "pending":
      return "waiting for the bytes";
    case "uploaded":
      return "uploaded, not scanned yet";
    case "scanning":
      return "scanning";
    case "active":
      return "ready";
    case "rejected":
      return "rejected by the scanner";
    case "quarantined":
      return "quarantined";
  }
}

export function StateBadge({ file }: { file: CreationFile }) {
  return <Badge tone={fileStateTone(file.state)}>{fileStateLabel(file.state)}</Badge>;
}

export function when(timestamp: string | undefined): string {
  if (!timestamp) {
    return "—";
  }
  const parsed = new Date(timestamp);
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleString();
}
